type AboutPageProps = {
  onBack: () => void;
};

export function AboutPage({ onBack }: AboutPageProps): React.JSX.Element {
  return (
    <main className="about-page">
      <button className="about-back" onClick={onBack}>
        <span aria-hidden="true">←</span>
        返回阅览室
      </button>

      <header className="about-hero">
        <div className="about-hero-copy">
          <p className="eyebrow">阅览室手记 · 002</p>
          <h1>做这个阅览室的初衷</h1>
          <p className="about-deck">
            每天都有新的报告、播客和观点涌进来，真正花时间读完一篇材料却越来越难。
            我们想给认真做研究的人留一处安静的地方，把值得细读的期刊和长谈整理好，
            保留原文，也保留查证的入口。
          </p>
        </div>
      </header>

      <section className="about-origin" aria-labelledby="about-origin-title">
        <div className="about-section-index">00</div>
        <div>
          <p className="eyebrow">WHY THIS EXISTS</p>
          <h2 id="about-origin-title">信息很多，能留下来的很少</h2>
          <div className="about-prose-columns">
            <p>
              现在每天能接触到的报告、快讯和自动生成的行业综述太多了。内容生产的门槛大幅降低后，
              真正有价值的一手事实反而容易淹没在重复的转述里。我们花很多时间刷完摘要，关掉页面后，
              常常说不清记住了什么，也找不到某个判断最初来自哪里。
            </p>
            <p>
              所以我们从减法开始。这里说的熵减很具体：去掉重复，留下完整内容；把散落的期刊文章、
              播客对话和来源链接整理到一起；需要核实时，可以很快回到原始出处。资料少一点噪声，
              研究者才有余地把一个问题想清楚。
            </p>
          </div>
        </div>
      </section>

      <section className="about-audiences" aria-label="服务对象">
        <article>
          <span className="about-section-index">01</span>
          <p className="eyebrow">FOR INSTITUTIONS</p>
          <h2>让做过的研究留在团队里</h2>
          <p>
            在智库和咨询机构做过项目的人都清楚，真正花时间的是理解背景、核对事实，以及逐步建立对行业的感觉。
            可项目一结束，读过的材料和查证过程很容易散落在个人文件夹、临时群聊与交付文档里。
            下一次遇到相似问题，团队又要重新找一遍。
          </p>
          <p>
            阅览室把期刊、播客逐字稿和来源链接按统一方式整理，让同事能查到前人读过什么、依据在哪里，
            也能接着已有线索往下研究。这样积累下来的资料会慢慢成为团队共同的工作记忆，
            不再跟着某个项目或某个人一起消失。
          </p>
        </article>

        <article>
          <span className="about-section-index">02</span>
          <p className="eyebrow">FOR INDIVIDUALS</p>
          <h2>帮个人研究者形成自己的看法</h2>
          <p>
            个人投资者每天都能看到大量观点，难的是判断哪些值得相信。只靠零散的新闻片段，
            很难形成稳定的理解；市场一波动，刚建立的看法也容易被最新一条消息推翻。
            这种不安很多时候来自手里缺少持续跟踪的材料。
          </p>
          <p>
            围绕自己关心的公司和行业，持续读原文、记住关键变化，也保留推翻旧判断的证据，
            时间久了才会有自己的尺度。我们想为愿意亲手找证据、反复核对的人，提供一个顺手的阅读工具，
            让每次阅读都能接上之前的积累。
          </p>
        </article>
      </section>

      <section className="about-ai" aria-labelledby="about-ai-title">
        <div>
          <span className="about-section-index">03</span>
          <p className="eyebrow">HUMAN × MACHINE</p>
        </div>
        <div>
          <h2 id="about-ai-title">技术负责整理，人负责判断</h2>
          <p>
            在这个系统里，AI 主要处理费时间的资料工作。它把几小时的外语播客转成文字，翻译长篇内容，
            再做切分、索引和主题关联。过去需要逐份搬运整理的材料，现在能更快进入可阅读、可检索的状态。
          </p>
          <p>
            模型给出的摘要和归纳只能作为阅读入口。资料说明了什么，证据是否可靠，结论需要附带哪些条件，
            仍然要由研究者读过原文后决定。AI 节省下来的时间，应该用在核实、比较和思考上。
          </p>
        </div>
      </section>

      <section className="about-principles" aria-labelledby="about-principles-title">
        <p className="eyebrow">WHAT WE KEEP</p>
        <h2 id="about-principles-title">我们日常坚持的几件事</h2>
        <ol>
          <li><span>01</span><strong>保留来源</strong><p>重要内容都附上原始链接，读者随时可以回到出处核对。</p></li>
          <li><span>02</span><strong>保留上下文</strong><p>摘要方便快速了解，完整文章和逐字稿负责交代前因后果。</p></li>
          <li><span>03</span><strong>方便串联</strong><p>按节目、期刊和主题整理，让不同时期的材料能互相印证。</p></li>
          <li><span>04</span><strong>认真取舍</strong><p>收录看研究价值，少追一时热度，也少增加无意义的重复。</p></li>
        </ol>
      </section>

      <footer className="about-closing">
        <p>
          研究本身没有捷径。做这个阅览室，只是想让那些愿意下笨功夫看材料的人，
          找资料时省一点时间，回头核对时少走一点弯路，也让认真读过的内容能够留下来。
        </p>
        <button onClick={onBack}>进入阅览室 <span aria-hidden="true">→</span></button>
      </footer>
    </main>
  );
}
